import TPAllocationDatasetService from 'src/services/tp-allocation/data-set/DatasetService';
import { DatasetRecord } from 'src/models/tp-allocation/DatasetRecord';
import Placeholder from 'src/models/tp-allocation/Placeholder';
import {
    CreateDASDataSourceRequest,
    DASDataSource,
    UpdateDASDataSourceRequest,
} from 'src/models/derived-allocation-sheet/DASDataSource';

/**
 * Maps Step 1 data sources to and from TPA dataset records, so the TPA dataset reducer, grid and COA
 * selector can be reused for derived allocation sheets.
 */
const datasetMapper = new TPAllocationDatasetService();

type DataSourceFields = Partial<Omit<CreateDASDataSourceRequest, 'sheetId'>>;

/** The members the TPA save request shares with the derived allocation sheet API. Others are not sent. */
const toDataSourceFields = (record: DatasetRecord, customCoaReference: Map<string, string>): DataSourceFields =>
    datasetMapper.convertToSaveDatasetRequest(record, customCoaReference, null as unknown as Map<string, Placeholder[]>, false);

export function toDatasetRecords(rows: DASDataSource[], sheetId: string) {
    const tpaRows = rows.map((row) => ({
        ...row,
        datasourceId: row.dataSourceId,
        datasourceType: row.dataSourceType,
        worksheetVersion: row.sheetVersion,
    }));
    return datasetMapper.convertToDatasetRecords(tpaRows, sheetId);
}

/** Create requires every member; one the row leaves blank is sent empty. The period members are sent only when set. */
export function toCreateDASDataSourceRequest(sheetId: string, record: DatasetRecord,
                                             customCoaReference: Map<string, string>): CreateDASDataSourceRequest {
    const fields = toDataSourceFields(record, customCoaReference);
    return {
        sheetId,
        dataSourceType: fields.dataSourceType ?? '',
        description: fields.description ?? '',
        currency: fields.currency ?? '',
        fxDate: fields.fxDate ?? '',
        fxType: fields.fxType ?? '',
        fxRate: fields.fxRate ?? '',
        dataKeys: fields.dataKeys ?? { companies: [], glAccounts: [] },
        ...(fields.period != null ? { period: fields.period } : {}),
        ...(fields.startPeriod != null ? { startPeriod: fields.startPeriod } : {}),
        ...(fields.endPeriod != null ? { endPeriod: fields.endPeriod } : {}),
    };
}

/**
 * Update sends the whole configuration with null for a cleared member, so that switching a range back to a
 * single period clears the range. With `dataUpdateOnly` the backend pulls the row's balance instead.
 */
export function toUpdateDASDataSourceRequest(sheetId: string, record: DatasetRecord, customCoaReference: Map<string, string>,
                                             dataUpdateOnly = false): UpdateDASDataSourceRequest {
    const fields = toDataSourceFields(record, customCoaReference);
    return {
        sheetId,
        dataSourceId: record.datasourceId,
        dataSourceType: fields.dataSourceType,
        description: fields.description,
        period: fields.period ?? null,
        startPeriod: fields.startPeriod ?? null,
        endPeriod: fields.endPeriod ?? null,
        fxDate: fields.fxDate ?? null,
        fxType: fields.fxType ?? null,
        fxRate: fields.fxRate ?? null,
        currency: fields.currency ?? null,
        ...(fields.dataKeys != null ? { dataKeys: fields.dataKeys } : {}),
        ...(dataUpdateOnly ? { dataUpdateOnly: true } : {}),
    };
}

/** The saved row as a dataset record; throws when the response carries no row. */
export function toSavedDatasetRecord(dataSource: DASDataSource | null, sheetId: string): DatasetRecord {
    if (!dataSource) {
        throw new Error('The data source could not be saved.');
    }
    return toDatasetRecords([dataSource], sheetId).records[0];
}
